import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { client, APIError } from "@/api"

/**
 * What happened to the creator's pending card-free trial when a comp landed.
 * `stripeCancelled: false` is the case to shout about: our row is cancelled
 * but Stripe refused, so the trial is still live there and nothing retries it.
 */
export type TrialCancellationResponse =
  | { kind: "no_orphan" }
  | { kind: "cancelled"; stripeSubscriptionId: string | null; stripeCancelled: boolean }

export type SubscriptionStatus = {
  plan_id: string
  /** The value stored on the row. NOT the answer to "does this team have access". */
  status: "active" | "inactive" | "suspended" | "cancelled"
  /**
   * ISO-8601 UTC, or null for a perpetual grant. For a billing-backed team this
   * is the CURRENT INVOICE PERIOD's end, not a terminal date, so it can sit
   * briefly in the past while a renewal webhook is in flight — read `expired`,
   * not this, to know whether the team has access.
   */
  expires_at: string | null
  /**
   * The grant has lapsed: its date has passed AND no live billing row backs it,
   * so it no longer entitles the team to anything (#1463) — even though `status`
   * still reads `active`, because that is what the row literally says and the
   * endpoint does not rewrite it.
   *
   * False for a perpetual (`expires_at: null`) grant, and false for a paid team
   * whose period boundary is stale.
   */
  expired: boolean
  plan: {
    id: string
    name: string
    tier: "individual" | "basic" | "pro" | "enterprise"
    max_games: number
    max_storage_gb: number
    max_team_members: number | null
    features: Record<string, any>
  }
}

export type TeamSubscriptionResponse = {
  ok: true
  team: {
    publicId: string
    name: string
  }
  subscription: SubscriptionStatus | null
}

export function useTeamSubscriptionStatus(teamPublicId: string | undefined) {
  return useQuery<TeamSubscriptionResponse>({
    queryKey: ["team-subscription-status", teamPublicId],
    enabled: !!teamPublicId,
    queryFn: async () => {
      const res = await (client.admin.subscriptions[":teamPublicId"] as any).status.$get({
        param: { teamPublicId: teamPublicId! },
      })
      if (!res.ok) {
        const err = (await res
          .clone()
          .json()
          .catch(() => ({}))) as any
        throw new APIError(
          res.status,
          err.code || "SUBSCRIPTION_STATUS_FAILED",
          new Error(err.message || "Failed to load subscription status")
        )
      }
      return res.json() as Promise<TeamSubscriptionResponse>
    },
  })
}

export type AttachSubscriptionPayload = {
  teamPublicId: string
  planId: string
  status?: "active" | "inactive" | "suspended" | "cancelled"
  expiresAt?: string
  /**
   * Opt in to a grant with no billing row behind it. The endpoint refuses an
   * ACTIVE attach on a plan that has a Stripe price — comp it instead — and this
   * is the only way past that. It exists for the case comp cannot cover: a plan
   * that is priced, but Stripe is unusable. Must be a literal `true`; the server
   * compares identity, not truthiness.
   */
  allowUnbackedGrant?: boolean
}

export type AttachSubscriptionResponse = {
  ok: true
  teamPublicId: string
  attachedPlanId: string
  status: "active" | "inactive" | "suspended" | "cancelled"
  /**
   * The team already holds a live subscription, so the access resolver will
   * recompute from it on the next Stripe event and replace this hand-grant.
   * The attach landed, but it is not durable — see #1387.
   */
  overriddenByBillingRow?: boolean
  billingRowPlanId?: string | null
  /** False means the rows are all dead — the resolver will REVOKE, not replace. */
  billingRowGrantsAccess?: boolean
}

export function useAttachTeamSubscription() {
  const qc = useQueryClient()
  return useMutation<AttachSubscriptionResponse, APIError, AttachSubscriptionPayload>({
    mutationFn: async ({
      teamPublicId,
      planId,
      status,
      expiresAt,
      allowUnbackedGrant,
    }: AttachSubscriptionPayload) => {
      const res = await (client.admin.subscriptions as any).attach.$post({
        json: { teamPublicId, planId, status, expiresAt, allowUnbackedGrant },
      })
      if (!res.ok) {
        const err = (await res
          .clone()
          .json()
          .catch(() => ({}))) as any
        throw new APIError(
          res.status,
          err.code || "SUBSCRIPTION_ATTACH_FAILED",
          new Error(err.message || "Failed to attach subscription")
        )
      }
      return res.json() as Promise<AttachSubscriptionResponse>
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["team-subscription-status", variables.teamPublicId] })
    },
  })
}

export type CompSubscriptionPayload = {
  teamPublicId: string
  planId: string
  /** Required. It is what marks the billing row as a comp, not just a note. */
  reason: string
  /**
   * `null` means "no expiry", explicitly stated. Omit the property entirely to
   * say nothing about the expiry — the endpoint reads the difference, and only
   * an omission counts as a retry of an existing comp.
   */
  expiresAt?: string | null
}

export type CompSubscriptionResponse = {
  ok: true
  teamPublicId: string
  planId: string
  reason: string
  stripeSubscriptionId: string
  stripeCustomerId: string
  expiresAt: string | null
  accessResolution: "granted" | "revoked" | "no_billing_rows"
  /** Set when the creator held a pending card-free trial that the comp replaced. */
  trialCancellation: TrialCancellationResponse
}

/**
 * Grant a comped plan as a real Stripe subscription at 100% off.
 *
 * Prefer this over `useAttachTeamSubscription`: an attach writes our database
 * only, so the grant is invisible to Stripe and can be silently revoked by a
 * webhook for an unrelated subscription (#1387).
 */
export function useCompTeamSubscription() {
  const qc = useQueryClient()
  return useMutation<CompSubscriptionResponse, APIError, CompSubscriptionPayload>({
    mutationFn: async (payload: CompSubscriptionPayload) => {
      const res = await (client.admin.subscriptions as any).comp.$post({ json: payload })
      if (!res.ok) {
        const err = (await res
          .clone()
          .json()
          .catch(() => ({}))) as any
        throw new APIError(
          res.status,
          err.code || "SUBSCRIPTION_COMP_FAILED",
          new Error(err.message || "Failed to comp subscription")
        )
      }
      return res.json() as Promise<CompSubscriptionResponse>
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["team-subscription-status", variables.teamPublicId] })
      qc.invalidateQueries({ queryKey: ["admin", "subscriptions"] })
      // The winning plan changed, so every limit derived from it did too.
      // Without this the header says the new plan while the game/member/storage
      // figures below keep quoting the old one until a refocus or reload.
      qc.invalidateQueries({ queryKey: ["limits", "team", variables.teamPublicId] })
    },
  })
}

export type RevokeCompResponse = {
  ok: true
  teamPublicId: string
  revokedPlanId: string
  stripeSubscriptionId: string | null
  accessResolution: "granted" | "revoked" | "no_billing_rows"
}

/** End a team's comp. Cancels it in Stripe, then re-derives the team's access. */
export function useRevokeTeamComp() {
  const qc = useQueryClient()
  return useMutation<RevokeCompResponse, APIError, { teamPublicId: string }>({
    mutationFn: async ({ teamPublicId }) => {
      const res = await (client.admin.subscriptions as any).comp.revoke.$post({
        json: { teamPublicId },
      })
      if (!res.ok) {
        const err = (await res
          .clone()
          .json()
          .catch(() => ({}))) as any
        throw new APIError(
          res.status,
          err.code || "SUBSCRIPTION_COMP_REVOKE_FAILED",
          new Error(err.message || "Failed to revoke comp")
        )
      }
      return res.json() as Promise<RevokeCompResponse>
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["team-subscription-status", variables.teamPublicId] })
      qc.invalidateQueries({ queryKey: ["admin", "subscriptions"] })
      // Revoking can change the winning plan too — the team may keep access from
      // another subscription, on different limits.
      qc.invalidateQueries({ queryKey: ["limits", "team", variables.teamPublicId] })
    },
  })
}

export type CreateTrialPayload = {
  teamPublicId: string
}

export type CreateTrialResponse = {
  ok: true
  message: string
  teamPublicId: string
  teamName: string
}

export function useCreateTeamTrial() {
  const qc = useQueryClient()
  return useMutation<CreateTrialResponse, APIError, CreateTrialPayload>({
    mutationFn: async ({ teamPublicId }: CreateTrialPayload) => {
      const res = await (client.admin.subscriptions as any)["create-trial"].$post({
        json: { teamPublicId },
      })
      if (!res.ok) {
        const err = (await res
          .clone()
          .json()
          .catch(() => ({}))) as any
        throw new APIError(
          res.status,
          err.code || "TRIAL_CREATE_FAILED",
          new Error(err.message || "Failed to create trial subscription")
        )
      }
      return res.json() as Promise<CreateTrialResponse>
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["team-subscription-status", variables.teamPublicId] })
    },
  })
}

export type UnbackedAccessTeam = {
  team_public_id: string
  team_name: string
  plan_id: string
  status: string
  expires_at: string | null
}

export type UnbackedAccessResponse = {
  ok: true
  teams: UnbackedAccessTeam[]
}

/**
 * Teams holding active access with no *granting* billing row behind it.
 *
 * Note "granting", not "any": a team whose only subscription is cancelled shows
 * up here alongside teams with no rows at all, because a dead subscription backs
 * nothing. That distinction is not cosmetic — the access resolver only skips a
 * team when it has ZERO billing rows, so a lone cancelled row leaves the team
 * one webhook away from having its access revoked (#1387, Ohio Prime).
 */
export function useUnbackedAccess() {
  return useQuery<UnbackedAccessResponse>({
    queryKey: ["admin", "subscriptions", "unbacked-access"],
    queryFn: async () => {
      const res = await (client.admin.subscriptions as any)["unbacked-access"].$get()
      if (!res.ok) {
        const err = (await res
          .clone()
          .json()
          .catch(() => ({}))) as any
        throw new APIError(
          res.status,
          err.code || "UNBACKED_ACCESS_FAILED",
          new Error(err.message || "Failed to load teams with unbacked access")
        )
      }
      return res.json() as Promise<UnbackedAccessResponse>
    },
  })
}
